import type {
    WorkerRevisionDecision,
    WorkerRevisionDecisionReason,
} from '../history/worker';

/**
 * Worker 过期响应错误详情。
 */
export interface StaleWorkerResponseErrorDetails {
    /** WorkerRevisionGuard 给出的拒绝原因。 */
    readonly reason: WorkerRevisionDecisionReason;
    /** Worker 请求 ID。 */
    readonly requestId: string;
    /** 请求发出时的文档 revision。 */
    readonly tokenDocumentRevision: number;
    /** 请求发出时的历史 epoch。 */
    readonly tokenHistorySeq: number;
    /** 响应返回时的当前文档 revision。 */
    readonly currentDocumentRevision: number;
    /** 响应返回时的当前历史 epoch。 */
    readonly currentHistorySeq: number;
}

/**
 * Worker 过期响应错误。
 *
 * 设计说明：
 * 1. 过期响应不是几何计算失败，而是 history / document epoch 推进后的预期丢弃结果；
 * 2. 仍然保留 Error 形态，便于显式 await 的调用方识别并中断后续写回；
 * 3. 通过独立类型区分普通 Worker 失败，避免把真实计算错误静默吞掉。
 */
export class StaleWorkerResponseError extends Error {
    /** 类型判别标记。 */
    public readonly isStaleWorkerResponseError = true;
    /** Worker 过期响应详情。 */
    public readonly details: StaleWorkerResponseErrorDetails;

    /**
     * @param details Worker 过期响应详情。
     */
    public constructor(details: StaleWorkerResponseErrorDetails) {
        super(
            `Worker 响应已过期并被丢弃：reason=${details.reason}, `
            + `request=${details.requestId}, `
            + `tokenRevision=${details.tokenDocumentRevision}/${details.tokenHistorySeq}, `
            + `currentRevision=${details.currentDocumentRevision}/${details.currentHistorySeq}`,
        );
        this.name = 'StaleWorkerResponseError';
        this.details = details;
    }
}

/**
 * 从 revision guard 决策创建 Worker 过期响应错误。
 *
 * @param decision revision guard 决策。
 * @returns Worker 过期响应错误。
 */
export function createStaleWorkerResponseError(decision: WorkerRevisionDecision): StaleWorkerResponseError {
    return new StaleWorkerResponseError({
        reason: decision.reason,
        requestId: decision.token.requestId,
        tokenDocumentRevision: decision.token.documentRevision,
        tokenHistorySeq: decision.token.historySeq,
        currentDocumentRevision: decision.state.documentRevision,
        currentHistorySeq: decision.state.historySeq,
    });
}

/**
 * 判断输入是否为 Worker 过期响应错误。
 *
 * @param error 待判断错误。
 * @returns 如果输入是 stale worker 响应错误则返回 true。
 */
export function isStaleWorkerResponseError(error: unknown): error is StaleWorkerResponseError {
    return error instanceof StaleWorkerResponseError
        || (typeof error === 'object'
            && error !== null
            && (error as {isStaleWorkerResponseError?: unknown}).isStaleWorkerResponseError === true);
}
